<div class="min-h-screen bg-gray-100">
  <!-- Top Header -->
  <app-navbar
    [currentUser]="currentUser()?.first_name + ' ' + currentUser()?.last_name || 'Doctor'"
    [userRole]="getUserRole()"
  ></app-navbar>

  <!-- Main Content Layout -->
  <div class="flex flex-col lg:flex-row min-h-[calc(100vh-64px)] page-container pt-16">
    <!-- Sidebar -->
    <app-sidebar [selectedMenu]="selectedMenu()" (menuSelect)="selectMenu($event)" class="sidebar-adaptive">
    </app-sidebar>

    <!-- Main Content Area -->
    <div class="flex-1 p-4 md:p-6 main-content">
      <!-- Page Header with Search -->
      <app-page-header [showFilter]="false" [showGreeting]="true"></app-page-header>

      <!-- Overview Section -->
      <div class="mb-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Overview</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <!-- Today's Appointments Card -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">Today's Appointments</p>
                <p class="text-3xl font-bold text-gray-900">{{ todayAppointments() }}</p>
              </div>
              <div class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  ></path>
                </svg>
              </div>
            </div>
          </div>

          <!-- Total Patients Card -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">Total Patients</p>
                <p class="text-3xl font-bold text-gray-900">{{ totalPatients() }}</p>
              </div>
              <div class="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  ></path>
                </svg>
              </div>
            </div>
          </div>

          <!-- Critical Care Card -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">Critical Care</p>
                <p class="text-3xl font-bold text-gray-900">{{ criticalCare() }}</p>
              </div>
              <div class="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  ></path>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Two Column Layout for Appointments and Patients Data -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Upcoming Appointments -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 class="text-lg font-semibold text-primary mb-4">Upcoming Appointments</h3>
          <div class="space-y-4">
            @for (appointment of upcomingAppointments(); track appointment.id) {
              <div class="border border-gray-200 rounded-lg p-4">
                <div class="flex items-start justify-between gap-4">
                  <div class="flex-1">
                    <p class="font-semibold text-gray-900">{{ appointment.time }}</p>
                    <p class="text-sm text-gray-600">Appointment scheduled for {{ appointment.patientName }}</p>
                    <p class="text-xs text-gray-500 mt-1">(patient) {{ appointment.status }}</p>
                  </div>
                  <div class="flex flex-col gap-2">
                    <button
                      (click)="openCancelModal(appointment)"
                      class="px-4 py-1.5 text-sm text-primary border border-primary rounded hover:bg-primary/10 whitespace-nowrap w-full"
                    >
                      Cancel
                    </button>
                    <button
                      (click)="openRescheduleModal(appointment)"
                      class="px-4 py-1.5 text-sm text-primary border border-primary rounded hover:bg-primary/10 whitespace-nowrap w-full"
                    >
                      Reschedule
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Patients Data Chart -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 class="text-lg font-semibold text-primary mb-4">Patients Data</h3>
          <div class="flex items-center justify-center h-64">
            <!-- Donut Chart -->
            <div class="relative w-48 h-48">
              <svg viewBox="0 0 100 100" class="transform -rotate-90">
                <!-- Out-patients (gray) - 40% -->
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#6B7280"
                  stroke-width="20"
                  stroke-dasharray="251.2"
                  stroke-dashoffset="0"
                  opacity="0.8"
                />
                <!-- In-patients (primary) - 60% -->
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#417BC6"
                  stroke-width="20"
                  stroke-dasharray="150.72 100.48"
                  stroke-dashoffset="0"
                />
              </svg>
            </div>
          </div>
          <div class="mt-4 space-y-2">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 bg-primary rounded-full"></div>
                <span class="text-sm text-gray-700">In-patients</span>
              </div>
              <span class="text-sm font-semibold text-gray-900">{{ inPatients() }}%</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 bg-gray-500 rounded-full"></div>
                <span class="text-sm text-gray-700">Out-patients</span>
              </div>
              <span class="text-sm font-semibold text-gray-900">{{ outPatients() }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Activities -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">Recent Activities</h3>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200">
                <th class="text-left py-3 px-4 text-sm font-semibold text-gray-700">S/N</th>
                <th class="text-left py-3 px-4 text-sm font-semibold text-gray-700">Activity</th>
                <th class="text-right py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                <th class="text-right py-3 px-4 text-sm font-semibold text-gray-700"></th>
              </tr>
            </thead>
            <tbody>
              @for (activity of recentActivities(); track activity.id; let i = $index) {
                <tr class="border-b border-gray-100 hover:bg-gray-50">
                  <td class="py-3 px-4 text-sm text-gray-600">{{ (i + 1).toString().padStart(2, '0') }}.</td>
                  <td class="py-3 px-4 text-sm text-gray-900">{{ activity.description }}</td>
                  <td class="py-3 px-4 text-sm text-gray-600 text-right">{{ activity.date }}</td>
                  <td class="py-3 px-4 text-right">
                    <button class="text-gray-400 hover:text-gray-600">
                      <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                        ></path>
                      </svg>
                    </button>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="4" class="py-8 text-center text-sm text-gray-500">No activities recorded yet.</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Chat Button -->
  <div class="fixed bottom-4 right-4 md:bottom-6 md:right-6">
    <button
      (click)="openChat()"
      class="w-10 h-10 md:w-12 md:h-12 bg-primary text-white rounded-full shadow-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200"
    >
      <svg class="w-5 h-5 md:w-6 md:h-6 mx-auto" fill="currentColor" viewBox="0 0 20 20">
        <path
          fill-rule="evenodd"
          d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
          clip-rule="evenodd"
        ></path>
      </svg>
    </button>
  </div>

  <!-- Chat Component -->
  <app-chat [isOpen]="isChatOpen()" (closeChat)="closeChat()"></app-chat>

  <!-- Cancel Appointment Modal -->
  <app-cancel-appointment-modal
    [isOpen]="isCancelModalOpen()"
    [appointment]="selectedAppointment()"
    (modalClose)="closeCancelModal()"
    (appointmentCancel)="handleCancelAppointment($event)"
  ></app-cancel-appointment-modal>

  <!-- Reschedule Appointment Modal -->
  <app-reschedule-appointment-modal
    [isOpen]="isRescheduleModalOpen()"
    [appointment]="selectedAppointment()"
    (modalClose)="closeRescheduleModal()"
    (reschedule)="handleRescheduleAppointment($event)"
  ></app-reschedule-appointment-modal>
</div>
